import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ClientLayout } from './components/layouts/ClientLayout';
import { AdminLayout } from './components/layouts/AdminLayout';
import { Dashboard } from './pages/client/Dashboard';
import { Tickets } from './pages/client/Tickets';
import { Messages } from './pages/client/Messages';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminTickets } from './pages/admin/AdminTickets';
import { AdminMessages } from './pages/admin/AdminMessages';
import { AdminReviews } from './pages/admin/AdminReviews';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { Home } from './pages/Home';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { UserSettingsPage } from './pages/UserSettingsPage';
import { NotFound } from './pages/NotFound';

const ProtectedRoute: React.FC<{ children: React.ReactNode; role?: 'admin' | 'staff' | 'client' }> = ({ children, role }) => {
  const { user, profile, loading } = useAuth();

  if (loading) return <div className="flex items-center justify-center h-screen bg-[#050505] text-white">Loading...</div>;
  if (!user) return <Navigate to="/login" />;

  // If specific role required, check it
  if (role && profile?.role !== role && profile?.role !== 'admin') return <Navigate to="/" />;

  // If no role specified (general protected route) but user is admin, redirect to admin panel
  if (!role && profile?.role === 'admin' && window.location.pathname.startsWith('/dashboard')) {
    return <Navigate to="/admin" />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      
      {/* Client Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <ClientLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="messages" element={<Messages />} />
        <Route path="settings" element={<UserSettingsPage />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute role="staff">
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="tickets" element={<AdminTickets />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="reviews" element={<AdminReviews />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}
